import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { toast } from "sonner";
import { BrowserRouter, Routes, Route, Navigate, useParams, Outlet } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import React, { lazy, Suspense } from "react";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AuthProvider } from "./contexts/AuthContext";
import { CustomThemeProvider } from "./contexts/ThemeContext";
import ThemeAestheticsController from "./components/aesthetics/ThemeAestheticsController";
import Layout from "./components/Layout";
import ScrollToTop from "./components/ScrollToTop";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { isFeatureEnabled } from "@/lib/feature-routes";
import { RequireAdmin } from "./components/RequireAdmin";
import type { FeatureFlags } from "@/types/site-config";

// Loading fallback component
const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-foreground" />
  </div>
);

// Redirect components for old /mixes routes
const RedirectMixSlug = () => {
  const { slug } = useParams();
  return <Navigate to={`/audio/${slug}`} replace />;
};

const RedirectMixEdit = () => {
  const { id } = useParams();
  return <Navigate to={`/admin/audio/edit/${id}`} replace />;
};

// Redirect components for old /photos routes
const RedirectGallerySlug = () => {
  const { slug } = useParams();
  return <Navigate to={`/galleries/${slug}`} replace />;
};

const RedirectPhotoDetail = () => {
  const { gallerySlug, photoId } = useParams();
  return <Navigate to={`/galleries/${gallerySlug}/${photoId}`} replace />;
};

// Lazy load all route components
const Index = lazy(() => import("./pages/Index"));
const Photos = lazy(() => import("./pages/Photos"));
const GalleryDetail = lazy(() => import("./pages/GalleryDetail"));
const PhotoDetail = lazy(() => import("./pages/PhotoDetail"));
const Writing = lazy(() => import("./pages/Writing"));
const Links = lazy(() => import("./pages/Links"));
const PostDetail = lazy(() => import("./pages/PostDetail"));
const CategoryIndex = lazy(() => import("./pages/CategoryIndex"));
const CategoryDetail = lazy(() => import("./pages/CategoryDetail"));
const TagIndex = lazy(() => import("./pages/TagIndex"));
const TagDetail = lazy(() => import("./pages/TagDetail"));
const Archive = lazy(() => import("./pages/Archive"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Admin = lazy(() => import("./pages/Admin"));
const SignIn = lazy(() => import("./pages/auth/SignIn"));
const SignOut = lazy(() => import("./pages/auth/SignOut"));
const Error = lazy(() => import("./pages/auth/Error"));
const BlogAdmin = lazy(() => import("./pages/admin/BlogAdmin"));
const BlogEditor = lazy(() => import("./pages/admin/BlogEditor"));
const LinksAdmin = lazy(() => import("./pages/admin/LinksAdmin"));
const GalleryAdmin = lazy(() => import("./pages/admin/GalleryAdmin"));
const GalleryEdit = lazy(() => import("./pages/admin/GalleryEdit"));
const NewGallery = lazy(() => import("./pages/admin/galleries/New"));
const PeopleList = lazy(() => import("./pages/admin/galleries/PeopleList"));
const PersonDetail = lazy(() => import("./pages/admin/galleries/PersonDetail"));
const Feeds = lazy(() => import("./pages/Feeds"));
const Audio = lazy(() => import("./pages/Audio"));
const AudioDetail = lazy(() => import("./pages/AudioDetail"));
const AudioAdmin = lazy(() => import("./pages/admin/AudioAdmin"));
const AudioEditor = lazy(() => import("./pages/admin/AudioEditor"));
const Store = lazy(() => import("./pages/Store"));
const StoreDetail = lazy(() => import("./pages/StoreDetail"));
const StoreCheckout = lazy(() => import("./pages/StoreCheckout"));
const StoreThanks = lazy(() => import("./pages/StoreThanks"));
const StoreOrderStatus = lazy(() => import("./pages/StoreOrderStatus"));
const StoreCollection = lazy(() => import("./pages/StoreCollection"));
const StoreSeason = lazy(() => import("./pages/StoreSeason"));
const StoreAdmin = lazy(() => import("./pages/admin/StoreAdmin"));
const StoreEditor = lazy(() => import("./pages/admin/StoreEditor"));
const StoreOrders = lazy(() => import("./pages/admin/StoreOrders"));
const StoreSettings = lazy(() => import("./pages/admin/StoreSettings"));
const StoreImport = lazy(() => import("./pages/admin/StoreImport"));
const StoreCollections = lazy(() => import("./pages/admin/StoreCollections"));
const StoreCollectionEditor = lazy(() => import("./pages/admin/StoreCollectionEditor"));
const StoreSeasons = lazy(() => import("./pages/admin/StoreSeasons"));
const StoreSeasonEditor = lazy(() => import("./pages/admin/StoreSeasonEditor"));
const AuditLogPage = lazy(() => import("./pages/admin/AuditLogPage"));
const TripsAdmin = lazy(() => import("./pages/admin/TripsAdmin"));
const TripImport = lazy(() => import("./pages/admin/TripImport"));
const TripDetail = lazy(() => import("./pages/admin/TripDetail"));
const JobsAdmin = lazy(() => import("./pages/admin/JobsAdmin"));
// Public participant trip view — standalone Memphis surface, rendered OUTSIDE
// the site Layout/theme (no Header/Footer/global nav). Token is the credential.
const TripTracker = lazy(() => import("./pages/TripTracker"));
const NotFound = lazy(() => import("./pages/NotFound"));

function FeatureGuard({ feature, children }: { feature: keyof FeatureFlags; children: React.ReactNode }) {
  const { data: config } = useSiteConfig();
  if (!config) return null;
  if (!isFeatureEnabled(feature, config.features)) return <NotFound />;
  return <>{children}</>;
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof Error) {
        // Detect 401 — session expired
        if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          toast.error('Session expired', {
            description: 'Please sign in again to continue.',
            action: {
              label: 'Sign In',
              onClick: () => window.location.href = '/auth/signin',
            },
          });
          return;
        }
        // Skip 404s — these are expected (e.g., navigating to non-existent post)
        if (error.message.includes('404')) return;
        // Show generic error toast for other failures
        toast.error('Something went wrong', {
          description: error.message,
        });
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      if (error instanceof Error) {
        if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          toast.error('Session expired', {
            description: 'Your session has expired. Please sign in again.',
            action: {
              label: 'Sign In',
              onClick: () => window.location.href = '/auth/signin',
            },
          });
          return;
        }
        toast.error('Save failed', {
          description: error.message,
        });
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: 2, // Only retry failed requests twice
      retryOnMount: false, // Don't retry when component mounts if query already failed
      refetchOnWindowFocus: false, // Don't refetch when window gains focus
      staleTime: 60000, // Consider data fresh for 1 minute
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange={false}
    >
      <CustomThemeProvider>
        <AuthProvider>
          <TooltipProvider>
            <ThemeAestheticsController />
            <Toaster />
            <Sonner />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <ScrollToTop />
              <ErrorBoundary fallback={
                <div className="mx-auto max-w-2xl p-8 text-center">
                  <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
                  <p className="text-muted-foreground mb-6">An unexpected error occurred. Please try refreshing the page.</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Refresh
                  </button>
                </div>
              }>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route element={<Layout />}>
                      <Route path="/" element={<Index />} />
                      <Route path="/galleries" element={<FeatureGuard feature="galleries"><Photos /></FeatureGuard>} />
                      <Route path="/galleries/:slug" element={<FeatureGuard feature="galleries"><GalleryDetail /></FeatureGuard>} />
                      <Route path="/galleries/:gallerySlug/:photoId" element={<FeatureGuard feature="galleries"><PhotoDetail /></FeatureGuard>} />

                      {/* Redirect old /photos routes to /galleries */}
                      <Route path="/photos" element={<Navigate to="/galleries" replace />} />
                      <Route path="/photos/:slug" element={<RedirectGallerySlug />} />
                      <Route path="/photos/:gallerySlug/:photoId" element={<RedirectPhotoDetail />} />

                      <Route path="/writing" element={<FeatureGuard feature="blog"><Writing /></FeatureGuard>} />
                      <Route path="/links" element={<FeatureGuard feature="links"><Links /></FeatureGuard>} />
                      {/* Audio routes (formerly Mixes) */}
                      <Route path="/audio" element={<FeatureGuard feature="mixes"><Audio /></FeatureGuard>} />
                      <Route path="/audio/:slug" element={<FeatureGuard feature="mixes"><AudioDetail /></FeatureGuard>} />

                      {/* Redirects from old /mixes paths */}
                      <Route path="/mixes" element={<Navigate to="/audio" replace />} />
                      <Route path="/mixes/:slug" element={<RedirectMixSlug />} />

                      {/* Store routes. "thanks" can never be a product slug: react-router
                          ranks the static /store/thanks above /store/:slug. */}
                      <Route path="/store" element={<FeatureGuard feature="store"><Store /></FeatureGuard>} />
                      <Route path="/store/collections/:slug" element={<FeatureGuard feature="store"><StoreCollection /></FeatureGuard>} />
                      <Route path="/store/seasons/:slug" element={<FeatureGuard feature="store"><StoreSeason /></FeatureGuard>} />
                      <Route path="/store/thanks" element={<FeatureGuard feature="store"><StoreThanks /></FeatureGuard>} />
                      <Route path="/store/orders/:token" element={<FeatureGuard feature="store"><StoreOrderStatus /></FeatureGuard>} />
                      <Route path="/store/:slug" element={<FeatureGuard feature="store"><StoreDetail /></FeatureGuard>} />
                      <Route path="/store/:slug/checkout" element={<FeatureGuard feature="store"><StoreCheckout /></FeatureGuard>} />

                      {/* Unified tag/category routes (all content types) */}
                      <Route path="/tags" element={<TagIndex />} />
                      <Route path="/tags/:slug" element={<TagDetail />} />
                      <Route path="/tags/:slug/page/:page" element={<TagDetail />} />
                      <Route path="/categories" element={<CategoryIndex />} />
                      <Route path="/categories/:slug" element={<CategoryDetail />} />
                      <Route path="/categories/:slug/page/:page" element={<CategoryDetail />} />

                      {/* Blog-specific tag/category routes (backwards compatibility) */}
                      <Route path="/writing/categories" element={<FeatureGuard feature="blog"><CategoryIndex /></FeatureGuard>} />
                      <Route path="/writing/categories/:slug" element={<FeatureGuard feature="blog"><CategoryDetail /></FeatureGuard>} />
                      <Route path="/writing/categories/:slug/page/:page" element={<FeatureGuard feature="blog"><CategoryDetail /></FeatureGuard>} />
                      <Route path="/writing/tags" element={<FeatureGuard feature="blog"><TagIndex /></FeatureGuard>} />
                      <Route path="/writing/tags/:slug" element={<FeatureGuard feature="blog"><TagDetail /></FeatureGuard>} />
                      <Route path="/writing/tags/:slug/page/:page" element={<FeatureGuard feature="blog"><TagDetail /></FeatureGuard>} />

                      <Route path="/writing/:slug" element={<FeatureGuard feature="blog"><PostDetail /></FeatureGuard>} />
                      <Route path="/archive" element={<Archive />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/feeds" element={<Feeds />} />
                      <Route path="/admin" element={<Admin />} />
                      <Route path="/auth/signin" element={<SignIn />} />
                      <Route path="/auth/signout" element={<SignOut />} />
                      <Route path="/auth/error" element={<Error />} />
                      <Route path="/admin/blog" element={<FeatureGuard feature="blog"><BlogAdmin /></FeatureGuard>} />
                      <Route path="/admin/blog/new" element={<FeatureGuard feature="blog"><BlogEditor /></FeatureGuard>} />
                      <Route path="/admin/blog/edit/:id" element={<FeatureGuard feature="blog"><BlogEditor /></FeatureGuard>} />
                      <Route path="/admin/links" element={<FeatureGuard feature="links"><LinksAdmin /></FeatureGuard>} />
                      <Route path="/admin/galleries" element={<FeatureGuard feature="galleries"><GalleryAdmin /></FeatureGuard>} />
                      <Route path="/admin/galleries/new" element={<FeatureGuard feature="galleries"><NewGallery /></FeatureGuard>} />
                      <Route path="/admin/galleries/edit/:id" element={<FeatureGuard feature="galleries"><GalleryEdit /></FeatureGuard>} />
                      <Route path="/admin/galleries/people" element={<FeatureGuard feature="galleries"><PeopleList /></FeatureGuard>} />
                      <Route path="/admin/galleries/people/:id" element={<FeatureGuard feature="galleries"><PersonDetail /></FeatureGuard>} />
                      {/* Admin audio routes */}
                      <Route path="/admin/audio" element={<FeatureGuard feature="mixes"><AudioAdmin /></FeatureGuard>} />
                      <Route path="/admin/audio/new" element={<FeatureGuard feature="mixes"><AudioEditor /></FeatureGuard>} />
                      <Route path="/admin/audio/edit/:id" element={<FeatureGuard feature="mixes"><AudioEditor /></FeatureGuard>} />
                      <Route path="/admin/mixes" element={<Navigate to="/admin/audio" replace />} />
                      <Route path="/admin/mixes/new" element={<Navigate to="/admin/audio/new" replace />} />
                      <Route path="/admin/mixes/edit/:id" element={<RedirectMixEdit />} />
                      {/* Admin store routes — reachable behind admin auth regardless of the
                          public `features.store` flag, so products can be curated (as drafts)
                          before the public storefront is switched on. */}
                      <Route path="/admin/store" element={<StoreAdmin />} />
                      <Route path="/admin/store/new" element={<StoreEditor />} />
                      <Route path="/admin/store/edit/:id" element={<StoreEditor />} />
                      <Route path="/admin/store/orders" element={<StoreOrders />} />
                      <Route path="/admin/store/settings" element={<StoreSettings />} />
                      <Route path="/admin/store/import" element={<StoreImport />} />
                      <Route path="/admin/store/collections" element={<StoreCollections />} />
                      <Route path="/admin/store/collections/new" element={<StoreCollectionEditor />} />
                      <Route path="/admin/store/collections/edit/:id" element={<StoreCollectionEditor />} />
                      <Route path="/admin/store/seasons" element={<StoreSeasons />} />
                      <Route path="/admin/store/seasons/new" element={<StoreSeasonEditor />} />
                      <Route path="/admin/store/seasons/edit/:id" element={<StoreSeasonEditor />} />
                      <Route path="/admin/audit" element={<AuditLogPage />} />
                      {/* Trips — private, single-user ops tracker. NOT feature-gated and
                          NOT in public nav/sitemap/feeds; RequireAdmin gates the UI and
                          every /api/admin/trips route independently enforces auth. */}
                      <Route path="/admin/trips" element={<RequireAdmin><TripsAdmin /></RequireAdmin>} />
                      <Route path="/admin/trips/import" element={<RequireAdmin><TripImport /></RequireAdmin>} />
                      <Route path="/admin/trips/:id" element={<RequireAdmin><TripDetail /></RequireAdmin>} />
                      <Route path="/admin/jobs" element={<RequireAdmin><JobsAdmin /></RequireAdmin>} />
                    </Route>
                    {/* Public participant trip view — standalone, no site chrome
                        or theme; must live OUTSIDE the <Layout> route above. */}
                    <Route path="/trips/:token" element={<TripTracker />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
            </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
      </CustomThemeProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
